import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useDebounce from "../../hooks/useDebounce";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import { Avatar, IconButton, Box, Select, MenuItem, FormControl, InputLabel, Typography, TextField } from "@mui/material";
import NotFound from "../../components/not-found/NotFound";
import Pagination from "react-js-pagination";
import { toastMessage } from "../../utils/toastMessage";
import findSerialNumber from "../../utils/findSerialNumber";
import { charLimit } from "../../utils/charLimit";
import { dateFormatWithTime } from "../../utils/dateFormat";
import { getLocalStorage, setLocalStorage } from "../../config/config";
import { getUserTransactionHistoryApi } from "../../services/userManagement";
import { SecondaryButton } from "../../components/Button/Button";
import common from "../../routes/common";

const { NOT_FOUND } = common;

const UserTransactionListing = ({ userId, username }) => {
  // State management
  const [transactionList, setTransactionList] = useState([]);
  const [total, setTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState(
    getLocalStorage("userTransactionList", "search") || ""
  );
  const [timeRange, setTimeRange] = useState("");
  const [timeRangeStartDate, setTimeRangeStartDate] = useState("");
  const [timeRangeEndDate, setTimeRangeEndDate] = useState("");
  
  // Statistics
  const [totalCredits, setTotalCredits] = useState(0);
  const [totalDebits, setTotalDebits] = useState(0);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [netBalance, setNetBalance] = useState(0);

  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const debounceValue = useDebounce(search || "", 300);

  // Time range options
  const timeRangeOptions = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "last7days", label: "Last 7 Days" },
    { value: "last30days", label: "Last 30 Days" },
    { value: "thisMonth", label: "This Month" },
    { value: "lastMonth", label: "Last Month" },
    { value: "custom", label: "Custom Range" }
  ];

  // Time range handler
  const handleTimeRangeChange = (value) => {
    setTimeRange(value);
    if (value !== 'custom') {
      setTimeRangeStartDate("");
      setTimeRangeEndDate("");
    }
  };

  // Time range date change handler
  const handleTimeRangeDateChange = (type, value) => {
    if (type === 'start') {
      setTimeRangeStartDate(value);
    } else {
      setTimeRangeEndDate(value);
    }
  };

  // Function to set localStorage values whenever the state variables change
  useEffect(() => {
    setLocalStorage("userTransactionList", "search", search);
  }, [search]);

  // Fetch user transaction history
  const fetchUserTransactionHistory = async () => {
    try {
      setTransactionList([]);
      showLoader();
      const sendData = {
        page: activePage,
        limit: pageSize,
        userId: userId
      };

      if (debounceValue) {
        sendData.search = debounceValue;
      }
      if (timeRange) {
        sendData.timeRange = timeRange;
      }
      if (timeRangeStartDate) {
        sendData.timeRangeStartDate = timeRangeStartDate;
      }
      if (timeRangeEndDate) {
        sendData.timeRangeEndDate = timeRangeEndDate;
      }

      const { data } = await getUserTransactionHistoryApi(sendData);

      if (data.code === 200) {
        setTransactionList(data?.result?.list || []);
        setTotal(data?.result?.total || 0);
        setTotalCredits(data?.result?.totalCredits || 0);
        setTotalDebits(data?.result?.totalDebits || 0);
        setTotalTransactions(data?.result?.totalTransactions || 0);
        setNetBalance(data?.result?.netBalance || 0);
      } else {
        setTransactionList([]);
        setTotal(0);
        toastMessage(data.message, "error", "transactionList");
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      hideLoader();
    }
  };

  // Handle page change
  const handlePageChange = (event) => {
    setActivePage(event);
  };

  // Filter handlers
  const clearFilters = () => {
    setSearch("");
    setTimeRange("");
    setTimeRangeStartDate("");
    setTimeRangeEndDate("");
    setActivePage(1);
  };

  // useEffect for fetching data
  useEffect(() => {
    if (userId) {
      fetchUserTransactionHistory();
    }
  }, [activePage, debounceValue, timeRange, timeRangeStartDate, timeRangeEndDate, pageSize, userId]);

  return (
    <>
      {loader}
      
          {/* Statistics Cards */}
          {/* <div className="indicator-tabs" style={{ marginBottom: '20px', display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <div className="indicator-item" style={{ 
              background: '#f0f0f0', 
              padding: '10px 20px', 
              borderRadius: '8px',
              textAlign: 'center',
              minWidth: '150px'
            }}>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#FF8989' }}>
                {totalTransactions}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>Total Transactions</div>
            </div>
            <div className="indicator-item" style={{ 
              background: '#f0f0f0', 
              padding: '10px 20px', 
              borderRadius: '8px',
              textAlign: 'center',
              minWidth: '150px'
            }}>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#4CAF50' }}>
                {totalCredits.toFixed(2)}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>Total Credits</div>
            </div>
            <div className="indicator-item" style={{ 
              background: '#f0f0f0', 
              padding: '10px 20px', 
              borderRadius: '8px',
              textAlign: 'center',
              minWidth: '150px'
            }}>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#F44336' }}>
                {totalDebits.toFixed(2)}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>Total Debits</div>
            </div>
            <div className="indicator-item" style={{ 
              background: '#f0f0f0', 
              padding: '10px 20px', 
              borderRadius: '8px',
              textAlign: 'center',
              minWidth: '150px'
            }}>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: netBalance >= 0 ? '#4CAF50' : '#F44336' }}>
                {netBalance.toFixed(2)}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>Net Balance</div>
            </div>
          </div> */}

          {/* Filters */}
           {/* <div className="FilterBox FilterArea" style={{ flexDirection: 'column' }}> 
            <div className="group_filter_value" style={{ 
              marginBottom: '15px', 
              display: 'flex !important', 
              flexWrap: 'wrap !important', 
              gap: '15px', 
              alignItems: 'center',
              width: '100%',
              flexDirection: 'row'
            }}>
              <div className="form-group search_md_size userSearch" style={{ minWidth: '200px', flex: '1 1 auto' }}>
                <input
                  type="search"
                  className={`form-control ${search && "active"}`}
                  placeholder="Search by transaction type, reason"
                  value={search || ""}
                  style={{ "--color": "#FF8989" }}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

                  <InputLabel>Time Range</InputLabel>
                  <Select
                    value={timeRange}
                    onChange={(e) => handleTimeRangeChange(e.target.value)}
                    label="Time Range"
                  >
                    <MenuItem value="">
                      <em>All Time Ranges</em>
                    </MenuItem>
                    {timeRangeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              
              {timeRange === 'custom' && (
                <>
                  <div className="form-group select_sm_width" style={{ minWidth: '150px', flex: '0 0 auto' }}>
                    <TextField
                      size="small"
                      type="date"
                      variant="outlined"
                      label="Start Date"
                      value={timeRangeStartDate}
                      onChange={(e) => handleTimeRangeDateChange('start', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </div>
                  <div className="form-group select_sm_width" style={{ minWidth: '150px', flex: '0 0 auto' }}>
                    <TextField
                      size="small"
                      type="date"
                      variant="outlined"
                      label="End Date"
                      value={timeRangeEndDate}
                      onChange={(e) => handleTimeRangeDateChange('end', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </div>
                </>
              )}

              <div className="form-group refresh_button" style={{ minWidth: '120px', flex: '0 0 auto', display: 'flex', gap: '10px' }}>
                <SecondaryButton onClick={() => fetchUserTransactionHistory()}>
                  Apply
                </SecondaryButton>
                <SecondaryButton onClick={clearFilters}>
                  Clear
                </SecondaryButton>
              </div>
            </div>
          </div>  */}

          {/* Transaction History Table */}
          <div className="content-table">
            <table>
              <thead>
                <tr>
                  <th>S. No.</th>
                  <th>Date & Time</th>
                  <th>Transaction Type</th>
                  <th>Transaction For</th>
                  <th>Amount</th>
                  <th>Consultant</th>
                  <th>Reason</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {transactionList.length > 0 ? (
                  transactionList.map((transaction, index) => (
                    <React.Fragment key={transaction._id || "---"}>
                      <tr>
                        <td>{findSerialNumber(index, activePage, pageSize) || "---"}</td>
                        <td>{dateFormatWithTime(transaction?.createdAt) || "---"}</td>
                        <td>
                          <span style={{ 
                            color: transaction?.transactionType === 0 ? '#4CAF50' : '#F44336',
                            fontWeight: 'bold'
                          }}>
                            {transaction?.transactionTypeText || "---"}
                          </span>
                        </td>
                        <td>{transaction?.transactionForText || "---"}</td>
                        <td style={{ 
                          color: transaction?.transactionType === 0 ? '#4CAF50' : '#F44336',
                          fontWeight: 'bold'
                        }}>
                          {transaction?.transactionType === 0 ? '+' : '-'}{transaction?.coins?.toFixed(2) || "0.00"}
                        </td>
                        <td>
                          {transaction?.consultantDetails ? (
                            <Link to={`/consultant-management/consultant-detail/${transaction?.consultantDetails?._id}`}>
                              {charLimit(transaction?.consultantDetails?.name, 30) || "---"}
                            </Link>
                          ) : (
                            "---"
                          )}
                        </td>
                        <td>{charLimit(transaction?.walletDeductReason, 50) || "---"}</td>
                        <td>
                          <span style={{ 
                            color: 'green',
                            fontWeight: 'bold'
                          }}>
                            Completed
                          </span>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))
                ) : (
                  <NotFound
                    cols={8}
                    msg="No transactions found!"
                    icon={NOT_FOUND}
                    loader={loader}
                  />
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination-wrapper mt-4 d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <Typography variant="body2" color="textSecondary">
                Show:
              </Typography>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(parseInt(e.target.value))}
                style={{ 
                  padding: '8px 12px', 
                  border: '1px solid #ccc', 
                  borderRadius: '4px',
                  fontSize: '14px',
                  minWidth: '80px'
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <Typography variant="body2" color="textSecondary">
                 per page
              </Typography>
            </div>
            
            <div className="d-flex align-items-center gap-2">
              <Typography variant="body2" color="textSecondary">
                Showing {((activePage - 1) * pageSize) + 1} to {Math.min(activePage * pageSize, total)} of {total} items
              </Typography>
            </div>
            
            {total > pageSize && (
              <div className="Pagination">
                <Pagination
                  activePage={activePage}
                  itemsCountPerPage={pageSize}
                  totalItemsCount={total}
                  pageRangeDisplayed={5}
                  onChange={handlePageChange}
                  itemClass="page-item"
                  linkClass="page-link"
                />
              </div>
            )}
          </div>
     
    </>
  );
};

export default UserTransactionListing;
